import { useState } from "react";
import {
  Alert,
  Button,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { FaInfoCircle, FaTicketAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Menu from "../../components/Menu";
import NavbarDashboard from "../../components/NavbarDashboard";
import { BillingTypeSelector } from "../../components/Subscriptions/BillingTypeSelector";
import { CouponInput } from "../../components/Subscriptions/CouponInput";
import { PlanCard } from "../../components/Subscriptions/PlanCard";
import "../../components/Subscriptions/Subscriptions.css";
import { useCoupon } from "../../hooks/useCoupon";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { usePlans } from "../../hooks/usePlans";
import { useSubscription } from "../../hooks/useSubscription";
import { useUserProfile } from "../../hooks/useUserProfile";
import { subscriptionService } from "../../services/subscriptionService";

/**
 * Página de seleção de planos
 */
export const PlansPage = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingType, setBillingType] = useState("CREDIT_CARD");
  const [showModal, setShowModal] = useState(false);
  const [isYoungLawyer, setIsYoungLawyer] = useState(false);
  const [youngLawyerFile, setYoungLawyerFile] = useState(null);
  const [isSubmittingYoungLawyer, setIsSubmittingYoungLawyer] = useState(false);
  const [youngLawyerLocalStatus, setYoungLawyerLocalStatus] = useState(null);
  const navigate = useNavigate();

  useFeatureFlag('subscriptions');

  const {
    currentSubscription,
    isLoading,
    subscribe,
    upgrade,
    isSubscribing,
    isUpgrading,
  } = useSubscription();

  const { profile, isLoading: isLoadingProfile } = useUserProfile();
  const { plans, isLoading: isLoadingPlans } = usePlans();
  const {
    couponCode,
    pricing,
    validateCoupon,
    removeCoupon,
    resetCoupon,
  } = useCoupon();

  // Verificar se CPF e celular estão preenchidos
  const userCpf = profile?.data?.cpf;
  const userCellphone = profile?.data?.cellphone;
  const hasRequiredData = userCpf && userCellphone;
  const jovemAdvogadoStatus = youngLawyerLocalStatus || profile?.data?.jovem_advogado_status || null;
  const jovemAdvogadoWarning = profile?.data?.jovem_advogado_warning || false;
  const jovemAdvogadoDaysRemaining = profile?.data?.jovem_advogado_days_remaining;
  const jovemAdvogadoAssignedCoupon = profile?.data?.jovem_advogado_assigned_coupon || null;
  const isYoungLawyerApproved = jovemAdvogadoStatus === "approved";
  const isYoungLawyerPending = jovemAdvogadoStatus === "pending";

  const handlePlanSelect = (planId) => {
    // Bloquear upgrades durante pending_cancellation
    if (isPendingCancellation) {
      return; // Não permite selecionar plano
    }

    // Bloquear se não tiver dados obrigatórios (CPF e celular)
    if (!hasRequiredData) {
      return; // Não permite selecionar plano
    }

    // Resetar cupom se mudou de plano
    if (selectedPlan !== planId && couponCode) {
      resetCoupon();
    }

    setSelectedPlan(planId);
    setIsYoungLawyer(false);
    setYoungLawyerFile(null);
    setShowModal(true);
  };

  const handleCouponApplied = async (code) => {
    const result = await validateCoupon(code, selectedPlan);
    return result;
  };

  const handleCouponRemoved = () => {
    removeCoupon();
  };

  const handleConfirmSubscription = () => {
    if (currentSubscription?.subscription) {
      // Já tem assinatura, fazer upgrade
      upgrade({ planId: selectedPlan, couponCode });
    } else {
      // Nova assinatura
      subscribe({ planId: selectedPlan, billingType, couponCode });
    }
    setShowModal(false);
    // Resetar cupom após confirmar
    resetCoupon();
  };

  const handleSubmitYoungLawyerRequest = async () => {
    if (!selectedPlan) return;
    if (!youngLawyerFile) return;

    try {
      setIsSubmittingYoungLawyer(true);
      const uploadResponse = await subscriptionService.uploadYoungLawyerOab(youngLawyerFile);
      const fileId = uploadResponse?.file_id;

      if (!fileId) {
        throw new Error("Falha ao obter ID do arquivo OAB");
      }

      const requestResponse = await subscriptionService.requestYoungLawyerApproval(
        selectedPlan,
        fileId,
        couponCode || null
      );

      if (requestResponse?.success) {
        setYoungLawyerLocalStatus("pending");
      }
    } catch (error) {
      console.error("Erro ao enviar solicitação de Jovem Advogado:", error);
    } finally {
      setIsSubmittingYoungLawyer(false);
    }
  };

  const currentPlanId = currentSubscription?.subscription?.plan_id || 1;
  const isOnTrial = currentSubscription?.is_on_trial || false;
  const trialEndsAt = currentSubscription?.trial_ends_at;
  const canHaveTrial = currentSubscription?.can_have_trial ?? true;
  const isPendingCancellation =
    currentSubscription?.is_pending_cancellation || false;
  const accessUntil = currentSubscription?.access_until;

  // Calcular dias restantes do trial
  const calculateTrialDaysRemaining = () => {
    if (!isOnTrial || !trialEndsAt) return 0;
    const today = new Date();
    const endDate = new Date(trialEndsAt);
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const trialDaysRemaining = calculateTrialDaysRemaining();

  if (false) {
    return (
      <>
        <NavbarDashboard />
        <section className="home">
          <Menu />
          <Container className="py-5">
            <Alert
              variant="info"
              style={{
                borderRadius: "16px",
                border: "2px solid #0dcaf0",
                padding: "3rem",
                textAlign: "center",
                maxWidth: "800px",
                margin: "0 auto",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
            >
              <FaInfoCircle size={64} className="text-info mb-4" />
              <h2 className="fw-bold mb-3">
                Funcionalidade em Desenvolvimento
              </h2>
              <p className="lead mb-4" style={{ fontSize: "1.1rem" }}>
                A funcionalidade de assinaturas e planos estará disponível em
                breve para todos os usuários.
              </p>
              <p className="text-muted mb-4">
                Estamos trabalhando para trazer a melhor experiência de gestão
                de planos e assinaturas para você. Em breve, você poderá
                escolher o plano ideal para suas necessidades e começar a
                aproveitar todos os recursos da plataforma.
              </p>
              <div className="mt-4">
                <Button
                  className="d-flex justify-content-center shadow-sm fs-6 p-2 rounded-2 fw-normal text-dark borderColor"
                  style={{
                    backgroundColor: "#DAA520",
                    justifySelf: "center",
                  }}
                  onClick={() => navigate("/meu-perfil")}
                >
                  Voltar ao Meu Perfil
                </Button>
              </div>
            </Alert>
          </Container>
        </section>
      </>
    );
  }

  if (isLoading || isLoadingProfile || isLoadingPlans) {
    return (
      <>
        <NavbarDashboard />
        <section className="home">
          <Menu />
          <Container className="py-5 text-center">
            <Spinner animation="border" variant="warning" />
            <p className="mt-3">Carregando planos...</p>
          </Container>
        </section>
      </>
    );
  }

  return (
    <>
      <NavbarDashboard />
      <section className="home">
        <Menu />
        <div style={{ padding: "2rem", maxWidth: "1400px", margin: "0 auto" }}>
          {/* Header Section */}
          <div className="text-center mb-5">
            <h1 className="display-4 fw-bold mb-3" style={{ color: "#2d3748" }}>
              Escolha seu Plano
            </h1>
            <p
              className="lead text-muted"
              style={{
                fontSize: "1.2rem",
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              Selecione o plano ideal para suas necessidades e comece a
              transformar seu escritório
            </p>

            {isOnTrial && (
              <Alert
                variant="info"
                className="mt-4 d-inline-flex align-items-center gap-2"
                style={{
                  borderRadius: "12px",
                  border: "2px solid #0dcaf0",
                  padding: "1rem 1.5rem",
                  fontSize: "1rem",
                }}
              >
                <FaInfoCircle size={20} />
                <span>
                  <strong>Período de Trial Ativo</strong> - Você tem{" "}
                  <strong>{trialDaysRemaining} dias</strong> restantes até{" "}
                  <strong>
                    {new Date(trialEndsAt).toLocaleDateString("pt-BR")}
                  </strong>
                </span>
              </Alert>
            )}

            {isPendingCancellation && accessUntil && (
              <Alert
                variant="warning"
                className="mt-4"
                style={{
                  borderRadius: "12px",
                  border: "2px solid #f0ad4e",
                  padding: "1.5rem",
                  fontSize: "1rem",
                  maxWidth: "700px",
                  margin: "1rem auto",
                  textAlign: "left",
                }}
              >
                <div className="d-flex align-items-start gap-3">
                  <FaInfoCircle size={24} className="text-warning mt-1" />
                  <div>
                    <h5 className="mb-2 fw-bold">
                      Assinatura com Cancelamento Agendado
                    </h5>
                    <p className="mb-2">
                      Sua assinatura foi cancelada, mas você ainda tem acesso
                      até{" "}
                      <strong>
                        {new Date(accessUntil).toLocaleDateString("pt-BR", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                        })}
                      </strong>
                      .
                    </p>
                    <p className="mb-0">
                      <strong>
                        Não é possível fazer upgrade durante este período.
                      </strong>{" "}
                      Para reativar sua assinatura ou assinar um novo plano,
                      aguarde o término do período de acesso ou entre em contato
                      com nosso suporte.
                    </p>
                  </div>
                </div>
              </Alert>
            )}

            {jovemAdvogadoWarning && (
              <Alert
                variant="warning"
                className="mt-4"
                style={{
                  borderRadius: "12px",
                  border: "2px solid #f0ad4e",
                  padding: "1.5rem",
                  fontSize: "1rem",
                  maxWidth: "700px",
                  margin: "1rem auto",
                  textAlign: "left",
                }}
              >
                <div className="d-flex align-items-start gap-3">
                  <FaInfoCircle size={24} className="text-warning mt-1" />
                  <div>
                    <h5 className="mb-2 fw-bold">
                      Benefício Jovem Advogado perto do limite
                    </h5>
                    <p className="mb-0">
                      Faltam <strong>{jovemAdvogadoDaysRemaining ?? 0} dias</strong> para o término da elegibilidade.
                      Entre em contato com a ADM para continuidade.
                    </p>
                  </div>
                </div>
              </Alert>
            )}

            {!hasRequiredData && (
              <Alert
                variant="danger"
                className="mt-4"
                style={{
                  borderRadius: "12px",
                  border: "2px solid #dc3545",
                  padding: "1.5rem",
                  fontSize: "1rem",
                  maxWidth: "700px",
                  margin: "1rem auto",
                  textAlign: "left",
                }}
              >
                <div className="d-flex align-items-start gap-3">
                  <FaInfoCircle size={24} className="text-danger mt-1" />
                  <div>
                    <h5 className="mb-2 fw-bold">
                      Complete seu Perfil para Assinar
                    </h5>
                    <p className="mb-2">
                      Para realizar uma assinatura, é necessário preencher os
                      seguintes dados em seu perfil:
                    </p>
                    <ul className="mb-3">
                      {!userCpf && <li>CPF</li>}
                      {!userCellphone && <li>Celular</li>}
                    </ul>
                    <Button
                      variant="danger"
                      onClick={() => navigate("/meu-perfil")}
                      style={{
                        borderRadius: "8px",
                        padding: "0.5rem 1.5rem",
                        fontWeight: "600",
                      }}
                    >
                      Ir para Meu Perfil
                    </Button>
                  </div>
                </div>
              </Alert>
            )}
          </div>

          {/* Plans Grid */}
          <Row className="g-4 justify-content-center mb-5">
            {plans.map((plan) => (
              <Col key={plan.id} xs={12} sm={6} lg={3}>
                <PlanCard
                  plan={plan}
                  isCurrentPlan={plan.id === currentPlanId}
                  isOnTrial={isOnTrial && plan.id === currentPlanId}
                  isPendingCancellation={
                    isPendingCancellation && plan.id === currentPlanId
                  }
                  hasAnyPendingCancellation={isPendingCancellation}
                  hasRequiredData={hasRequiredData}
                  onSelect={handlePlanSelect}
                  isLoading={isSubscribing || isUpgrading}
                  highlighted={plan.id === 3} // Destacar plano Gold
                />
              </Col>
            ))}
          </Row>

          {/* Modal de Confirmação */}
          <Modal show={showModal} onHide={() => setShowModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>
                {currentSubscription?.subscription
                  ? "Fazer Upgrade"
                  : "Confirmar Assinatura"}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              {!currentSubscription?.subscription && (
                <>
                  {/* Cupom Input */}
                  <CouponInput
                    planId={selectedPlan}
                    onCouponApplied={handleCouponApplied}
                    onCouponRemoved={handleCouponRemoved}
                    disabled={isSubscribing || isUpgrading}
                  />

                  {/* Resumo de Valores */}
                  {pricing && (
                    <div
                      className="mb-3 p-3 border rounded bg-light"
                      style={{ fontSize: "0.95rem" }}
                    >
                      <div className="d-flex align-items-center mb-2">
                        <FaTicketAlt className="me-2 text-success" />
                        <strong>Resumo de Valores</strong>
                      </div>
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted">Valor original:</span>
                        <span>
                          R${" "}
                          {Number(pricing.original_value)
                            .toFixed(2)
                            .replace(".", ",")}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between mb-1 text-success">
                        <span>Desconto ({pricing.discount_percentage}%):</span>
                        <span className="fw-bold">
                          - R${" "}
                          {Number(pricing.discount_value)
                            .toFixed(2)
                            .replace(".", ",")}
                        </span>
                      </div>
                      <hr className="my-2" />
                      <div className="d-flex justify-content-between">
                        <strong>Valor final:</strong>
                        <strong
                          className="text-success"
                          style={{ fontSize: "1.2rem" }}
                        >
                          R${" "}
                          {Number(pricing.final_value)
                            .toFixed(2)
                            .replace(".", ",")}
                        </strong>
                      </div>
                    </div>
                  )}

                  {canHaveTrial ? (
                    <>
                      <Alert variant="success">
                        <FaInfoCircle className="me-2" />
                        Você iniciará um período de{" "}
                        <strong>trial de 365 dias gratuitos</strong> com todos os
                        recursos do plano selecionado!
                      </Alert>
                      <BillingTypeSelector
                        value={billingType}
                        onChange={setBillingType}
                      />
                      <Alert variant="info" className="mt-3 mb-0">
                        <small>
                          <strong>Nota:</strong> O pagamento só será cobrado
                          após o término do período de trial.
                        </small>
                      </Alert>
                    </>
                  ) : (
                    <>
                      <Alert variant="warning">
                        <FaInfoCircle className="me-2" />
                        Você já utilizou o período de trial anteriormente. A
                        cobrança será realizada imediatamente.
                      </Alert>
                      <BillingTypeSelector
                        value={billingType}
                        onChange={setBillingType}
                      />
                      <Alert variant="info" className="mt-3 mb-0">
                        <small>
                          <strong>Nota:</strong> O pagamento será processado
                          assim que a assinatura for confirmada.
                        </small>
                      </Alert>
                    </>
                  )}

                  <div className="mt-3 p-3 border rounded">
                    <Form.Check
                      type="checkbox"
                      id="jovem-advogado-check"
                      label="Sou Jovem Advogado (quero solicitar benefício de cupom por até 5 anos de carteira OAB)"
                      checked={isYoungLawyer}
                      onChange={(e) => setIsYoungLawyer(e.target.checked)}
                      disabled={isSubscribing || isUpgrading || isYoungLawyerPending || isYoungLawyerApproved}
                    />

                    {isYoungLawyerApproved && (
                      <Alert variant="success" className="mt-3 mb-0">
                        Benefício aprovado. Cupom liberado:{" "}
                        <strong>{jovemAdvogadoAssignedCoupon?.code || "N/A"}</strong>
                      </Alert>
                    )}

                    {isYoungLawyerPending && (
                      <Alert variant="info" className="mt-3 mb-0">
                        Sua solicitação de Jovem Advogado está pendente de análise da ADM.
                      </Alert>
                    )}

                    {isYoungLawyer && !isYoungLawyerPending && !isYoungLawyerApproved && (
                      <>
                        <Form.Group className="mt-3">
                          <Form.Label>Documento da OAB (PDF/JPG/PNG/WEBP, até 10MB)</Form.Label>
                          <Form.Control
                            type="file"
                            accept=".pdf,image/jpeg,image/png,image/webp"
                            onChange={(e) => setYoungLawyerFile(e.target.files?.[0] || null)}
                          />
                        </Form.Group>
                        <Button
                          className="mt-3"
                          variant="outline-primary"
                          onClick={handleSubmitYoungLawyerRequest}
                          disabled={!youngLawyerFile || isSubmittingYoungLawyer}
                        >
                          {isSubmittingYoungLawyer ? "Enviando para análise..." : "Enviar para análise"}
                        </Button>
                      </>
                    )}
                  </div>
                </>
              )}

              {currentSubscription?.subscription && (
                <>
                  <p>
                    Deseja fazer upgrade do plano{" "}
                    <strong>
                      {plans.find((p) => p.id === currentPlanId)?.title ||
                        "N/A"}
                    </strong>{" "}
                    para o plano{" "}
                    <strong>
                      {plans.find((p) => p.id === selectedPlan)?.title || "N/A"}
                    </strong>
                    ?
                  </p>
                  <Alert variant="info" className="mb-0">
                    O valor será ajustado proporcionalmente ao período já
                    utilizado.
                  </Alert>
                </>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowModal(false)}
                disabled={isSubscribing || isUpgrading}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmSubscription}
                disabled={
                  isSubscribing ||
                  isUpgrading ||
                  (isYoungLawyer && !isYoungLawyerApproved)
                }
              >
                {isSubscribing || isUpgrading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Processando...
                  </>
                ) : (
                  "Confirmar"
                )}
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      </section>
    </>
  );
};
